import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { Buffer } from 'buffer';
import pcbStackup from 'pcb-stackup';
import {
  getGerberFiles,
  decodeGerberContent,
  type GerberFile,
} from '../../services/pcbService';

// Ensure Buffer is available globally for pcb-stackup
if (typeof window !== 'undefined') {
  (window as unknown as { Buffer: typeof Buffer }).Buffer = Buffer;
}

interface PCBViewerProps {
  partNumber: string;
  isOpen: boolean;
  onClose: () => void;
  /** When true, renders without modal wrapper for embedding in sliding panels */
  embedded?: boolean;
}

// Gerber file extensions (lowercase)
const GERBER_EXTENSIONS = [
  '.gtl', '.gbl', '.gts', '.gbs', '.gto', '.gbo',
  '.gtp', '.gbp', '.gko', '.gm1', '.gm2', '.gm3',
  '.drl', '.xln', '.ger', '.gbr', '.pho'
];

// Check if a file has a valid Gerber extension
function hasValidGerberExtension(filename: string): boolean {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
  return GERBER_EXTENSIONS.includes(ext);
}

// Convert SVG string to canvas ImageData by rendering in an offscreen container
async function svgToCanvas(svgString: string, targetWidth: number, targetHeight: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    // Parse SVG
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svg = doc.querySelector('svg');

    if (!svg) {
      reject(new Error('Invalid SVG'));
      return;
    }

    // Get original dimensions
    let origWidth = parseFloat(svg.getAttribute('width') || '0');
    let origHeight = parseFloat(svg.getAttribute('height') || '0');

    if (!origWidth || !origHeight) {
      const viewBox = svg.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.split(/\s+/);
        if (parts.length >= 4) {
          origWidth = parseFloat(parts[2]);
          origHeight = parseFloat(parts[3]);
        }
      }
    }

    if (!origWidth) origWidth = targetWidth;
    if (!origHeight) origHeight = targetHeight;

    // Ensure required attributes
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    svg.setAttribute('width', String(origWidth));
    svg.setAttribute('height', String(origHeight));

    // Convert all styles to inline to ensure they render
    const styleElements = svg.querySelectorAll('style');
    styleElements.forEach(style => {
      // Keep style elements as they are - they should be embedded
    });

    // Serialize
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);

    // Create image from SVG
    const img = new Image();

    // Use blob URL for better compatibility with complex SVGs
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Create canvas at target size
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Fill with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      // Calculate scale to fit
      const scale = Math.min(targetWidth / origWidth, targetHeight / origHeight);
      const scaledWidth = origWidth * scale;
      const scaledHeight = origHeight * scale;

      // Center the image
      const offsetX = (targetWidth - scaledWidth) / 2;
      const offsetY = (targetHeight - scaledHeight) / 2;

      // Draw the image
      ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

      resolve(canvas);
    };

    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      logger.error('SVG to image error:', err);
      reject(err);
    };

    // Set crossOrigin before src
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

// Extract dimensions from SVG string
function getSvgDimensions(svgString: string): { width: number; height: number } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svg = doc.querySelector('svg');

  if (!svg) return { width: 800, height: 600 };

  // Try to get width/height from attributes
  let width = parseFloat(svg.getAttribute('width') || '0');
  let height = parseFloat(svg.getAttribute('height') || '0');

  // If not available, try viewBox
  if (!width || !height) {
    const viewBox = svg.getAttribute('viewBox');
    if (viewBox) {
      const parts = viewBox.split(/\s+/);
      if (parts.length >= 4) {
        width = parseFloat(parts[2]);
        height = parseFloat(parts[3]);
      }
    }
  }

  return { width: width || 800, height: height || 600 };
}

export default function PCBViewer({ partNumber, isOpen, onClose, embedded = false }: PCBViewerProps) {
  const { t } = useTranslation('bom');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topSvg, setTopSvg] = useState<string | null>(null);
  const [bottomSvg, setBottomSvg] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const loadGerberFiles = useCallback(async () => {
    if (!partNumber || !isOpen) return;

    setLoading(true);
    setError(null);
    setTopSvg(null);
    setBottomSvg(null);

    try {
      // Fetch Gerber files from API
      const response = await getGerberFiles(partNumber);

      if (!response.files || response.files.length === 0) {
        setError(t('pcbViewer.noGerberFiles'));
        return;
      }

      // Filter to only valid Gerber files
      const validFiles = response.files.filter((file: GerberFile) =>
        hasValidGerberExtension(file.name)
      );

      if (validFiles.length === 0) {
        setError(t('pcbViewer.noGerberFiles'));
        return;
      }

      // Convert to pcb-stackup format
      // ALL files use 'gerber' property - the library auto-detects file type
      const layers = validFiles.map((file: GerberFile) => ({
        filename: file.name.toLowerCase(),
        gerber: decodeGerberContent(file.content),
      }));

      // Generate PCB stackup SVG
      const stackup = await pcbStackup(layers);

      setTopSvg(stackup.top.svg);
      setBottomSvg(stackup.bottom.svg);
    } catch (err) {
      logger.error('Failed to load Gerber files:', err);
      setError(err instanceof Error ? err.message : t('pcbViewer.loadError'));
    } finally {
      setLoading(false);
    }
  }, [partNumber, isOpen, t]);

  useEffect(() => {
    if (isOpen) {
      loadGerberFiles();
    }
  }, [isOpen, loadGerberFiles]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.25, 5));
  const handleZoomOut = () => setZoom((z) => Math.max(z / 1.25, 0.25));
  const handleZoomReset = () => setZoom(1);

  // Generate preview image (shows before download)
  const handleGeneratePreview = async () => {
    if (!topSvg && !bottomSvg) return;

    setSaving(true);
    try {
      const jpgDataUrl = await generateJpgDataUrl();
      setPreviewDataUrl(jpgDataUrl);
      setShowPreview(true);
    } catch (err) {
      logger.error('Failed to generate preview:', err);
      alert(t('pcbViewer.previewError', 'Failed to generate preview'));
    } finally {
      setSaving(false);
    }
  };

  // Generate JPG data URL from SVGs
  const generateJpgDataUrl = async (): Promise<string> => {
    // Get dimensions from SVGs
    const topDim = topSvg ? getSvgDimensions(topSvg) : { width: 400, height: 300 };
    const bottomDim = bottomSvg ? getSvgDimensions(bottomSvg) : { width: 400, height: 300 };

    // Use the larger dimensions for consistent sizing, with minimum size
    const maxWidth = Math.max(topDim.width, bottomDim.width, 400);
    const maxHeight = Math.max(topDim.height, bottomDim.height, 300);

    // Scale up for better quality
    const scale = 2;
    const scaledWidth = maxWidth * scale;
    const scaledHeight = maxHeight * scale;

    // Create canvas for combined image (side by side with labels)
    const padding = 40 * scale;
    const labelHeight = 60 * scale;
    const gap = 60 * scale;
    const canvasWidth = (scaledWidth * 2) + gap + (padding * 2);
    const canvasHeight = scaledHeight + labelHeight + (padding * 2);

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not create canvas context');
    }

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw labels
    ctx.font = `bold ${48 * scale}px Arial, sans-serif`;
    ctx.textAlign = 'center';

    // Top label (blue)
    ctx.fillStyle = '#2563eb';
    ctx.fillText(t('pcbViewer.topSide', 'Top'), padding + (scaledWidth / 2), padding + 50 * scale);

    // Bottom label (green)
    ctx.fillStyle = '#16a34a';
    ctx.fillText(t('pcbViewer.bottomSide', 'Bottom'), padding + scaledWidth + gap + (scaledWidth / 2), padding + 50 * scale);

    // Draw SVGs - convert each to a canvas first, then draw
    const yOffset = padding + labelHeight;

    if (topSvg) {
      try {
        const topCanvas = await svgToCanvas(topSvg, scaledWidth, scaledHeight);
        ctx.drawImage(topCanvas, padding, yOffset);
      } catch (err) {
        logger.error('Failed to render top SVG:', err);
        // Draw placeholder
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(padding, yOffset, scaledWidth, scaledHeight);
        ctx.fillStyle = '#9ca3af';
        ctx.font = '24px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Top view unavailable', padding + scaledWidth / 2, yOffset + scaledHeight / 2);
      }
    }

    if (bottomSvg) {
      try {
        const bottomCanvas = await svgToCanvas(bottomSvg, scaledWidth, scaledHeight);
        ctx.drawImage(bottomCanvas, padding + scaledWidth + gap, yOffset);
      } catch (err) {
        logger.error('Failed to render bottom SVG:', err);
        // Draw placeholder
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(padding + scaledWidth + gap, yOffset, scaledWidth, scaledHeight);
        ctx.fillStyle = '#9ca3af';
        ctx.font = '24px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Bottom view unavailable', padding + scaledWidth + gap + scaledWidth / 2, yOffset + scaledHeight / 2);
      }
    }

    // Add part number watermark at bottom
    ctx.font = `${28 * scale}px Arial, sans-serif`;
    ctx.fillStyle = '#6b7280';
    ctx.textAlign = 'right';
    ctx.fillText(partNumber, canvasWidth - padding, canvasHeight - 16 * scale);

    // Convert to JPG
    return canvas.toDataURL('image/jpeg', 0.95);
  };

  // Download the preview image
  const handleDownload = () => {
    if (!previewDataUrl) return;

    const link = document.createElement('a');
    link.download = `${partNumber}_PCB.jpg`;
    link.href = previewDataUrl;
    link.click();
  };

  // Close preview modal
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewDataUrl(null);
  };

  // Content that's shared between embedded and modal modes
  const viewerContent = (
    <>
      {/* Controls - Zoom and Save */}
      <div className="flex items-center justify-between gap-2 p-4 border-b bg-gray-50 dark:bg-gray-700">
        {/* Save as JPG Button - Opens Preview */}
        <div>
          <button
            onClick={handleGeneratePreview}
            disabled={saving || (!topSvg && !bottomSvg)}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t('pcbViewer.saveAsJpg', 'Save as JPG')}
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('pcbViewer.generating', 'Generating...')}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{t('pcbViewer.saveAsJpg', 'Save as JPG')}</span>
              </>
            )}
          </button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500"
            title="Zoom Out"
          >
            -
          </button>
          <button
            onClick={handleZoomReset}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 min-w-[60px]"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            onClick={handleZoomIn}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500"
            title="Zoom In"
          >
            +
          </button>
        </div>
      </div>

      {/* Content - Side by Side View */}
      <div className="flex-1 overflow-auto p-4 bg-gray-100 dark:bg-gray-900 min-h-[600px]">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('pcbViewer.loadingGerber', 'Loading Gerber files...')}</p>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
            <button
              onClick={loadGerberFiles}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        )}

        {!loading && !error && (topSvg || bottomSvg) && (
          <div className="grid grid-cols-2 gap-4 h-full">
            {/* Top Side */}
            <div className="flex flex-col">
              <div className="text-center mb-2">
                <span className="inline-block px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                  {t('pcbViewer.topSide', 'Top')}
                </span>
              </div>
              <div className="flex-1 flex items-center justify-center bg-white dark:bg-gray-800 rounded shadow-inner overflow-auto min-h-[550px]">
                {topSvg ? (
                  <div
                    className="pcb-svg-container p-4"
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: 'center center',
                    }}
                    dangerouslySetInnerHTML={{ __html: topSvg }}
                  />
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t('pcbViewer.noPreview', 'No preview available')}</p>
                )}
              </div>
            </div>

            {/* Bottom Side */}
            <div className="flex flex-col">
              <div className="text-center mb-2">
                <span className="inline-block px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-full">
                  {t('pcbViewer.bottomSide', 'Bottom')}
                </span>
              </div>
              <div className="flex-1 flex items-center justify-center bg-white dark:bg-gray-800 rounded shadow-inner overflow-auto min-h-[550px]">
                {bottomSvg ? (
                  <div
                    className="pcb-svg-container p-4"
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: 'center center',
                    }}
                    dangerouslySetInnerHTML={{ __html: bottomSvg }}
                  />
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t('pcbViewer.noPreview', 'No preview available')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && !error && !topSvg && !bottomSvg && (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 dark:text-gray-400">{t('pcbViewer.noPreview', 'No preview available')}</p>
          </div>
        )}
      </div>

      <style>{`
        .pcb-svg-container svg {
          max-width: 100%;
          height: auto;
          display: block;
        }
      `}</style>
    </>
  );

  // Embedded mode: render content without modal wrapper
  if (embedded) {
    return (
      <div className="flex flex-col h-full">
        {viewerContent}
      </div>
    );
  }

  // Modal mode: full modal wrapper - wider for side-by-side view
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col mx-4">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <h2 className="text-lg font-semibold dark:text-white">
              {t('pcbViewer.title', 'PCB Preview')} - {partNumber}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
              title={t('common.close', 'Close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {viewerContent}

          {/* Footer */}
          <div className="flex justify-end gap-2 p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-500"
            >
              {t('common.close', 'Close')}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal - Shows generated JPG before download */}
      {showPreview && previewDataUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-[95vw] max-h-[95vh] flex flex-col mx-4">
            {/* Preview Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold dark:text-white">
                {t('pcbViewer.previewTitle', 'Preview')} - {partNumber}_PCB.jpg
              </h3>
              <button
                onClick={handleClosePreview}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                title={t('common.close', 'Close')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Preview Image */}
            <div className="flex-1 overflow-auto p-4 bg-gray-100 dark:bg-gray-900">
              <img
                src={previewDataUrl}
                alt={`${partNumber} PCB Preview`}
                className="max-w-full h-auto mx-auto shadow-lg rounded"
              />
            </div>

            {/* Preview Footer */}
            <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <button
                onClick={handleClosePreview}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-500"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('pcbViewer.download', 'Download')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
